import React, { useState, useEffect, useMemo, useRef } from 'react';
// import Select, { SingleValue, components } from 'react-select';
import {filteredTeacherByCourseId , changeTeacher, changeOfflineTeacher } from '../../../../../Api/apiUtils';
import Select, { SingleValue, components, ControlProps, StylesConfig } from 'react-select';
// import {filteredTeacherByCourseId , changeTeacher } from '../../../../../Api/apiUtils';
import { toast } from 'react-toastify';
import './style.scss';
import { Badge, Button, DescriptionList, Field, SearchInput, Select as NativeSelect } from '../../../../../ui';
import type { BadgeTone } from '../../../../../ui';
import { Dialog, TextArea } from '../ui-pending';
import { MixpanelEvents,trackEvent } from '../../../../../utils/mixpanelTracking';
import FindTeacher, {
  maybePostTeacherAssignmentAlert,
} from '../findTeacher';


const TEACHER_CAPACITY_LIMIT = 70;
const PAYOUT_TIER_ORDER = ["SILVER", "GOLD", "PLATINUM", "DIAMOND"];
const PAYOUT_TIER_TONES: Record<string, BadgeTone> = {
  SILVER: "success",
  GOLD: "warning",
  PLATINUM: "primary",
  DIAMOND: "info",
};

const CHANGE_REASON_OPTIONS = [
  { value: "", label: "-- Select a reason --" },
  ...[
    "Unavailability of preferred slots",
    "Unhappy due to multiple class cancellations",
    "Unhappy with the teaching experience",
    "Teacher resigned",
    "Teacher terminated",
    "Language Barrier",
    "Course Transfer",
    "Gender preference",
    "On Teacher Request",
    "Due to Level Change",
    "Not aligned by Sales",
    "Disengaged/Inactive Learner",
    "Bulk Teacher Change",
    "Teacher Change due to Academic Request",
    // "Other" (please specify) is disabled.
  ].map((reason) => ({ value: reason, label: reason })),
];

/** react-select themed with design-system tokens. */
const dsSelectStyles: StylesConfig<any, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: "var(--ds-control-md)",
    borderRadius: "var(--ds-radius-sm)",
    borderColor: state.isFocused ? "var(--ds-primary)" : "var(--ds-border-strong)",
    boxShadow: state.isFocused ? "var(--ds-focus-ring)" : "none",
    backgroundColor: "var(--ds-surface)",
    fontSize: "14px",
  }),
  singleValue: (base) => ({ ...base, color: "var(--ds-text)" }),
  input: (base) => ({ ...base, color: "var(--ds-text)" }),
  placeholder: (base) => ({ ...base, color: "var(--ds-text-subtle)" }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: "var(--ds-radius-sm)",
    boxShadow: "var(--ds-shadow-lift)",
    backgroundColor: "var(--ds-surface)",
    color: "var(--ds-text)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "var(--ds-primary)" : state.isFocused ? "var(--ds-primary-soft)" : "var(--ds-surface)",
    color: state.isSelected ? "var(--ds-text-on-dark)" : "var(--ds-text)",
  }),
};

const teacherSelectStyles: StylesConfig<any, false> = {
  ...dsSelectStyles,
  option: (base, state) => ({
    ...base,
    padding: "var(--ds-space-5) var(--ds-space-6)",
    color: "var(--ds-text)",
    backgroundColor: state.isDisabled
      ? "var(--ds-surface-muted)"
      : state.isFocused
        ? "var(--ds-primary-soft)"
        : "var(--ds-surface)",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
  }),
};

interface TeacherOption {
  value: string;
  label: string;
  workingStatus: 'ACTIVE' | 'RESIGNED' | 'TERMINATED' | string;
  allocationPriority?: string;
  activeStudentCount?: number;
  payoutCategory?: string;
  teacherLevels?: string[];
  isDisabled?: boolean;
}
interface CourseItem {
  courseName: string;
  courseId: string;
  isDemoCourse?: boolean;
  teacherName?: string;
  batchOfflineCourse?: boolean;
  courseEnrollmentId?: string | number;
  enrollmentId?: string | number;
}

interface SubscriptionProps {
  profileData:any;
  courseData?: CourseItem[] | null;
  studentId?: any;
  fetchCourses: () => Promise<void>;
}

interface Teacher {
  teacherId: any;
  teacherName: string;
  allocationPriority: string;
  workingStatus:any
  enableForPaid:any
  emailId: string;
  mobileNumber: string;
  profilePhoto?: string;
  credentialId?: string;
  activeStudentCount?: string | number;
  activeStudents?: string | number;
  payoutCategory?: string;
  teacherLevels?: string[] | string | null;
}

const getActiveStudentCount = (teacher: {
  activeStudentCount?: string | number;
  activeStudents?: string | number;
}): number => {
  const value = Number(teacher.activeStudentCount ?? teacher.activeStudents ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const getPayoutTier = (category?: string): string =>
  String(category || "OTHER").trim().toUpperCase();

const formatTeacherLevel = (level: string): string => {
  const cleaned = String(level || "").trim();
  if (!cleaned) return "";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
};

const getTeacherLevels = (
  levels?: string[] | string | null
): string[] => {
  const list = Array.isArray(levels)
    ? levels
    : typeof levels === "string"
      ? levels.split(",")
      : [];
  return list.map(formatTeacherLevel).filter(Boolean);
};

const PayoutCategoryBadge = ({ category }: { category?: string }) => {
  const tier = getPayoutTier(category);
  if (!category || tier === "OTHER") return null;
  return <Badge tone={PAYOUT_TIER_TONES[tier] || "neutral"}>{tier}</Badge>;
};

const TeacherMenuList = (props: any) => {
  const searchValue = String(props.selectProps.teacherSearch ?? "");
  const onSearch = props.selectProps.onTeacherSearch as
    | ((value: string) => void)
    | undefined;
  const searchRef = props.selectProps.teacherSearchRef as
    | React.RefObject<HTMLInputElement>
    | undefined;

  return (
    <div className="teacher-select-menu">
      <div
        className="teacher-select-search"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <SearchInput
          ref={searchRef}
          size="sm"
          aria-label="Search teachers"
          value={searchValue}
          placeholder="Search teacher name..."
          onChange={(e) => onSearch?.(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </div>
      <components.MenuList {...props}>{props.children}</components.MenuList>
    </div>
  );
};

const Subscription: React.FC<SubscriptionProps> = ({ courseData, studentId, fetchCourses, profileData }) => {
  console.log("teacherchange", courseData)
  const courses: CourseItem[] = Array.isArray(courseData) ? courseData : [];
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [findTeacherModal, setFindTeacherModal]= useState<boolean>(false);
  const [selectedCourse, setSelectedCourse] = useState<{ 
    value: string; 
    label: string;
    teacherName?: string;
    batchOfflineCourse?: boolean;
    enrollmentId?: string | number;
  } | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherOption | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [changeReason, setChangeReason] = useState<string>('');

  const [otherReason, setOtherReason] = useState("");
  const [reasonDescription, setReasonDescription] = useState("");
  const [courseMenuIsOpen, setCourseMenuIsOpen] = useState<boolean>(false);
  const [teacherMenuIsOpen, setTeacherMenuIsOpen] = useState<boolean>(false);
  const [teacherSearch, setTeacherSearch] = useState("");
  const teacherSearchRef = useRef<HTMLInputElement | null>(null);
  const [findTeacherSearchResults, setFindTeacherSearchResults] = useState<
    Array<{
      payoutCategory?: string;
      activeStudents?: string | number;
      activeStudentCount?: string | number;
    }>
  >([]);

console.log("selectedteacher", selectedTeacher)
  
  // Custom Control component to ensure entire control area opens dropdown on click
  // This preserves all original react-select styling and behavior
  const CustomControl = (props: ControlProps<any, false>) => {
    const { innerProps, selectProps } = props;
    const menuIsOpen = selectProps.menuIsOpen;
    const onMenuOpen = selectProps.onMenuOpen;
    
    return (
      <components.Control
        {...props}
        innerProps={{
          ...innerProps,
          onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;
            const isTypingInInput = !!target.closest("input");
            // Let the search input keep focus and show typed text.
            if (isTypingInInput) {
              if (innerProps.onMouseDown) innerProps.onMouseDown(e as any);
              if (!menuIsOpen && onMenuOpen) onMenuOpen();
              return;
            }
            // Open menu when clicking anywhere in the control if it's not already open
            if (!menuIsOpen && onMenuOpen) {
              e.preventDefault();
              e.stopPropagation();
              onMenuOpen();
            } else if (innerProps.onMouseDown) {
              innerProps.onMouseDown(e as any);
            }
          },
          onClick: (e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;
            const isTypingInInput = !!target.closest("input");
            if (isTypingInInput) {
              if (innerProps.onClick) innerProps.onClick(e as any);
              if (!menuIsOpen && onMenuOpen) onMenuOpen();
              return;
            }
            if (!menuIsOpen && onMenuOpen) {
              e.preventDefault();
              e.stopPropagation();
              onMenuOpen();
            } else if (innerProps.onClick) {
              innerProps.onClick(e as any);
            }
          }
        }}
      />
    );
  };

  const CustomOption = (props: any) => {
    const { data, isDisabled } = props;
    const activeCount = getActiveStudentCount(data);
    const atCapacity = isDisabled || activeCount > TEACHER_CAPACITY_LIMIT;
    const teacherLevels = getTeacherLevels(data.teacherLevels);
    const statusText =
      data?.allocationPriority == "1"
        ? `PRIORITY - ${data.workingStatus}`
        : data.workingStatus;
    return (
      <components.Option {...props} isDisabled={atCapacity}>
        <div
          className={`teacher-select-option${atCapacity ? " teacher-select-option--capacity" : ""}`}
        >
          <div className="teacher-select-option__body">
            <div className="teacher-select-option__title">
              <span className="teacher-select-option__name">{data.label.split(' - ')[0]}</span>
              <PayoutCategoryBadge category={data.payoutCategory} />
              {atCapacity && <Badge tone="danger">At capacity</Badge>}
            </div>
            {teacherLevels.length > 0 && (
              <div className="teacher-select-option__levels">
                {teacherLevels.map((level) => (
                  <Badge key={level}>{level}</Badge>
                ))}
              </div>
            )}
          </div>
          <div
            className={`teacher-select-option__meta${atCapacity ? " teacher-select-option__meta--capacity" : ""}`}
          >
            <span className="teacher-select-option__count">{activeCount} active</span>
            <span className="teacher-select-option__status">{statusText}</span>
          </div>
        </div>
      </components.Option>
    );
  };

  const CustomGroup = (props: any) => {
    const count = React.Children.count(props.children);
    if (!count) return null;
    return <components.Group {...props} />;
  };

  const CustomGroupHeading = (props: any) => (
    <div className="teacher-select-group-heading">{props.data.label}</div>
  );

  const teacherSelectOptions = useMemo(() => {
    const grouped = new Map<string, TeacherOption[]>();
    const query = teacherSearch.trim().toLowerCase();

    (Array.isArray(teachers) ? teachers : [])
      .filter(
        (teacher) =>
          teacher.workingStatus === "ACTIVE" && teacher.enableForPaid === "true"
      )
      .filter((teacher) => {
        if (!query) return true;
        const name = String(teacher.teacherName || "").toLowerCase();
        const category = getPayoutTier(teacher.payoutCategory).toLowerCase();
        const levels = getTeacherLevels(teacher.teacherLevels)
          .join(" ")
          .toLowerCase();
        return (
          name.includes(query) ||
          category.includes(query) ||
          levels.includes(query)
        );
      })
      .sort((a, b) => {
        const priorityA = a.allocationPriority === "1" ? 0 : 1;
        const priorityB = b.allocationPriority === "1" ? 0 : 1;
        return priorityA - priorityB;
      })
      .forEach((teacher) => {
        const activeStudentCount = getActiveStudentCount(teacher);
        const payoutCategory = getPayoutTier(teacher.payoutCategory);
        const option: TeacherOption = {
          value: teacher.teacherId,
          label: `${teacher.teacherName} - ${teacher.workingStatus}`,
          workingStatus: teacher.workingStatus,
          allocationPriority: teacher.allocationPriority,
          activeStudentCount,
          payoutCategory,
          teacherLevels: getTeacherLevels(teacher.teacherLevels),
          isDisabled: activeStudentCount > TEACHER_CAPACITY_LIMIT,
        };
        if (!grouped.has(payoutCategory)) grouped.set(payoutCategory, []);
        grouped.get(payoutCategory)!.push(option);
      });

    return Array.from(grouped.entries())
      .sort(([tierA], [tierB]) => {
        const indexA = PAYOUT_TIER_ORDER.indexOf(tierA);
        const indexB = PAYOUT_TIER_ORDER.indexOf(tierB);
        if (indexA === -1 && indexB === -1) return tierA.localeCompare(tierB);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      })
      .map(([tier, options]) => ({
        label: tier,
        options,
      }));
  }, [teachers, teacherSearch]);
  


   useEffect(() => {
        if (!selectedCourse) return;

        const fetchTeachers = async () => {
        

            try {
                const courseId = selectedCourse.value;
                const alwaysIncludeTestTeachers = process.env.REACT_APP_ALWAYS_INCLUDE_TEST_TEACHERS === 'true';

                const payload = {
                    page: 0,
                    size: 500,
                    courseId:courseId,
                    ...((alwaysIncludeTestTeachers) && { type: 'test' }),
                };

        


                const response = await filteredTeacherByCourseId( payload);
                if (response.data && Array.isArray(response.data.content)) {
                    setTeachers(response.data.content);
                } else {
                  
                    setTeachers([]);
                }
            } catch (error) {
                console.error("Error fetching teachers:", error);
           
                setTeachers([]);
            } finally {
              
            }
        };

        fetchTeachers();
    }, [selectedCourse]);

    useEffect(() => {
      if (!teacherMenuIsOpen) return;
      const id = window.setTimeout(() => teacherSearchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }, [teacherMenuIsOpen]);

    // Handle outside clicks to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Check if click is outside both select components and their menus
            const isClickInsideSelect = target.closest('.react-select-container') || 
                                       target.closest('[class*="react-select"]') ||
                                       target.closest('[id*="react-select"]') ||
                                       target.closest('[class*="css-"]'); // react-select uses css- prefixed classes
            
            if (!isClickInsideSelect) {
                setCourseMenuIsOpen(false);
                setTeacherMenuIsOpen(false);
                setTeacherSearch("");
            }
        };

        if (courseMenuIsOpen || teacherMenuIsOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [courseMenuIsOpen, teacherMenuIsOpen]);

  const handleCourseChange = (newValue: SingleValue<{ value: string; label: string }>) => {
    
    if (!newValue) {
      setSelectedCourse(null);
      setSelectedTeacher(null);
      setCourseMenuIsOpen(false);
      return;
    }
    
    const fullCourseData = courses.find(course => course.courseId === newValue.value);
    setSelectedCourse({
      value: newValue.value,
      label: newValue.label,
      teacherName: fullCourseData?.teacherName || 'No teacher assigned',
      batchOfflineCourse: fullCourseData?.batchOfflineCourse || false,
      enrollmentId:
        fullCourseData?.courseEnrollmentId ?? fullCourseData?.enrollmentId,
    });
    setSelectedTeacher(null);
    setCourseMenuIsOpen(false);
  };

  const handleSubmit = () => {
    if (!selectedCourse || !selectedTeacher) {
      toast.error('Please select a course and a teacher.');
      return;
    }
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!changeReason) {
      toast.error('Please provide a reason for changing the teacher.');
      return;
    }
    setIsSubmitting(true); // Start loading
    try {
      const isOfflineCourse = selectedCourse?.batchOfflineCourse || false;
      
      // Prepare payload based on course type
      const payload = isOfflineCourse ? {
        studentId: studentId,
        courseId: selectedCourse?.value,
        newTeacherId: selectedTeacher?.value,
        reasons: changeReason === "Other" ? otherReason : changeReason,
        reasonDescription,
      } : {
        teacherId: selectedTeacher?.value,
        courseId: selectedCourse?.value,
        studentId: studentId,
        reasons: changeReason === "Other" ? otherReason : changeReason,
        reasonDescription,
      };

      
      
            trackEvent(MixpanelEvents.TEACHER_CHANGE, {
              page:"Teacher Change in student profile",
              oldTeacherName:selectedCourse?.teacherName,
              newTeacherNAame:selectedTeacher?.label,
              newTeacherId: selectedTeacher?.value,
              courseId: selectedCourse?.value,
              studentId: studentId,
              reasons: changeReason,
         
            });
          
      if (isOfflineCourse) {
        await changeOfflineTeacher(payload);
      } else {
        await changeTeacher(payload);
      }

      await maybePostTeacherAssignmentAlert({
        enrollmentId: selectedCourse?.enrollmentId,
        teacherId: selectedTeacher?.value,
        assignedPayoutCategory: selectedTeacher?.payoutCategory,
        availableTeachers: [...teachers, ...findTeacherSearchResults],
      });
      
      setIsModalOpen(false);
      setSelectedCourse(null);
      setSelectedTeacher(null);
      setChangeReason('');
      setOtherReason('');
      setReasonDescription('');
      fetchCourses();
      toast.success('Teacher changed successfully!');
    } catch (error) {
      console.error('Failed to change teacher:', error);
      toast.error('Failed to change teacher. Please try again.');
    } finally{
      setIsSubmitting(false); 
    }
  };

  const courseOptions = courses
    .filter(course => !course.isDemoCourse)
    .map(course => ({
      value: course.courseId,
      label: course.batchOfflineCourse ? `${course.courseName} - offline` : course.courseName,
      teacherName: course.teacherName || 'No teacher assigned',
      batchOfflineCourse: course.batchOfflineCourse || false
    }));

  return (
    <div className="teacher-change">
      <h2 className="ds-card__title">Teacher change</h2>
      <div className="teacher-change__form">
        {/* Course Dropdown */}
        <Field label="Course">
          {(ctl) => (
            <Select
              inputId={ctl.id}
              value={selectedCourse}
              onChange={handleCourseChange}
              options={courseOptions}
              placeholder="Select a course"
              className="react-select-container"
              classNamePrefix="react-select"
              menuIsOpen={courseMenuIsOpen}
              onMenuOpen={() => setCourseMenuIsOpen(true)}
              onMenuClose={() => setCourseMenuIsOpen(false)}
              components={{ Control: CustomControl }}
              styles={dsSelectStyles}
            />
          )}
        </Field>

        {/* Current Teacher Display - Shown when course is selected */}
        {selectedCourse && (
          <DescriptionList items={[{ label: "Current teacher", value: selectedCourse.teacherName }]} />
        )}

        {selectedCourse && (
          <>
            <Field label="New teacher" hint="Select a teacher from the drop down or find a teacher.">
              {(ctl) => (
                <Select<TeacherOption, false>
                  inputId={ctl.id}
                  value={selectedTeacher}
                  onChange={(newValue: SingleValue<TeacherOption>) => {
                    if (!newValue || newValue.isDisabled) return;
                    setSelectedTeacher(newValue);
                    setTeacherSearch("");
                    setTeacherMenuIsOpen(false);
                  }}
                  options={teacherSelectOptions}
                  placeholder="Select a new teacher Or find a teacher"
                  classNamePrefix="react-select"
                  className="react-select-container teacher-select"
                  isSearchable={false}
                  {...({
                    teacherSearch,
                    onTeacherSearch: setTeacherSearch,
                    teacherSearchRef,
                  } as object)}
                  isOptionDisabled={(option: TeacherOption) =>
                    !!option.isDisabled || getActiveStudentCount(option) > TEACHER_CAPACITY_LIMIT
                  }
                  components={{
                    Option: CustomOption,
                    Control: CustomControl,
                    Group: CustomGroup,
                    GroupHeading: CustomGroupHeading,
                    MenuList: TeacherMenuList,
                  }}
                  menuIsOpen={teacherMenuIsOpen}
                  onMenuOpen={() => setTeacherMenuIsOpen(true)}
                  onMenuClose={() => {
                    if (document.activeElement === teacherSearchRef.current) return;
                    setTeacherMenuIsOpen(false);
                    setTeacherSearch("");
                  }}
                  openMenuOnClick
                  styles={teacherSelectStyles}
                />
              )}
            </Field>
            <div className="teacher-change__find">
              <Button variant="secondary" size="sm" onClick={() => setFindTeacherModal(true)}>
                Find Teacher
              </Button>
            </div>

            {/* <Label><b>New Teacher</b></Label>
            <Select
              value={selectedTeacher}
              onChange={(newValue) => setSelectedTeacher(newValue)}
              options={teachers
                .filter(teacher => teacher.workingStatus === "ACTIVE") // Filter only active teachers
                .sort((a, b) => {
                  // Move allocationPriority "1" to top
                  const priorityA = a.allocationPriority === "1" ? 0 : 1;
                  const priorityB = b.allocationPriority === "1" ? 0 : 1;
                  return priorityA - priorityB;
                }).map(teacher => ({
                  value: teacher.teacherId,
                  label: `${teacher.teacherName} - ${teacher.workingStatus}`,
                  workingStatus: teacher.workingStatus,
                  allocationPriority: teacher.allocationPriority
                }))
              }
              placeholder="Select a new teacher"
              classNamePrefix="react-select"
              components={{ Option: CustomOption }}
            /> */}
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!selectedTeacher}
              block
            >
              Request Teacher Change
            </Button>
          </>
        )}
      </div>

      {/* Change Reason Modal */}
      <Dialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Reason for changing teacher"
        actions={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={
                !changeReason ||
                !reasonDescription ||
                (changeReason === "Other" && !otherReason) ||
                isSubmitting
              }
              loading={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Confirm Change'}
            </Button>
          </>
        }
      >
        <Field label="Reason">
          {(ctl) => (
            <NativeSelect
              {...ctl}
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              required
              options={CHANGE_REASON_OPTIONS}
            />
          )}
        </Field>

        <Field label="Reason description" required>
          {(ctl) => (
            <TextArea
              {...ctl}
              value={reasonDescription}
              onChange={(e) => setReasonDescription(e.target.value)}
              placeholder="Please describe your reason (mandatory)"
              required
            />
          )}
        </Field>

        {changeReason === "Other" && (
          <Field label="Please specify" required>
            {(ctl) => (
              <TextArea
                {...ctl}
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Please explain your reason"
                required={changeReason === "Other"}
              />
            )}
          </Field>
        )}
      </Dialog>
      <Dialog
        isOpen={findTeacherModal}
        onClose={() => setFindTeacherModal(false)}
        size="xl"
        title="Find teacher"
        className="find-teacher-dialog"
      >
        <FindTeacher
          studentId={studentId}
          profileData={profileData}
          preselectedCourseId={selectedCourse?.value}
          onTeachersLoaded={setFindTeacherSearchResults}
          onTeacherSelect={(teacher) => {
            setSelectedTeacher({
              value: teacher.teacherId,
              label: `${teacher.firstName} ${teacher.lastName}`,
              workingStatus: "ACTIVE",
              allocationPriority: teacher.allocationPriority,
              payoutCategory: teacher.payoutCategory,
              activeStudentCount: getActiveStudentCount(teacher),
            });
            setFindTeacherModal(false);
          }}
          disableCourseSelect={true}
        />
      </Dialog>
    </div>
  );
};

export default Subscription;
